import { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  LayoutDashboard,
  Users,
  Store,
  CreditCard,
  Layers,
  DollarSign,
  Server,
  FileText,
  Megaphone,
  LifeBuoy,
  Menu,
  MessageSquare,
  RefreshCw,
  Receipt,
  Sparkles,
  User,
  LogOut,
  ChevronsUpDown,
  type LucideIcon,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminQuotePendingCount } from '@/hooks/queries/useAdminQuoteRequests';
import { useAdminSupportOpenCount } from '@/hooks/queries/useAdminSupportTickets';
import { useAdminDashboard } from '@/hooks/queries/useAdminDashboard';
import { BRAND } from '@/lib/brand';
import { adminPath, isNavPathActive } from '@/lib/hosts';

interface NavItem {
  icon: LucideIcon;
  labelKey: string;
  href: string;
  badge?: 'quotes' | 'subscriptions' | 'support';
}

interface NavGroup {
  labelKey?: string;
  items: NavItem[];
}

const adminNavGroups: NavGroup[] = [
  {
    items: [{ icon: LayoutDashboard, labelKey: 'admin_dashboard', href: '/admin' }],
  },
  {
    labelKey: 'sidebar_group_management',
    items: [
      { icon: Users, labelKey: 'users', href: '/users' },
      { icon: Store, labelKey: 'shops', href: '/shops' },
      {
        icon: CreditCard,
        labelKey: 'subscriptions',
        href: '/subscriptions',
        badge: 'subscriptions',
      },
      { icon: Layers, labelKey: 'plans', href: '/plans' },
      { icon: FileText, labelKey: 'quote_requests', href: '/quotes', badge: 'quotes' },
      { icon: LifeBuoy, labelKey: 'support_tickets', href: '/support', badge: 'support' },
    ],
  },
  {
    labelKey: 'sidebar_group_operations',
    items: [
      { icon: Receipt, labelKey: 'payments', href: '/payments' },
      { icon: DollarSign, labelKey: 'finance', href: '/finance' },
      { icon: RefreshCw, labelKey: 'sync', href: '/sync' },
      { icon: Sparkles, labelKey: 'admin_ai_styles', href: '/ai/document-styles' },
      { icon: MessageSquare, labelKey: 'admin_messaging', href: '/messaging' },
      { icon: Megaphone, labelKey: 'announcements', href: '/announcements' },
    ],
  },
  {
    labelKey: 'sidebar_group_system',
    items: [
      { icon: Server, labelKey: 'system', href: '/system/logs' },
      { icon: Activity, labelKey: 'system_health', href: '/system/health' },
    ],
  },
];

function AdminRailNavItem({
  item,
  expanded,
  currentUrl,
  badgeCount,
}: {
  item: NavItem;
  expanded: boolean;
  currentUrl: string;
  badgeCount?: number;
}) {
  const { t } = useTranslation('admin');
  const itemHref = adminPath(item.href);
  const isActive = isNavPathActive(currentUrl, itemHref);
  const Icon = item.icon;

  if (!expanded) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={itemHref}
            className={cn(
              'relative flex h-10 w-full items-center justify-center transition-colors duration-100',
              isActive
                ? 'bg-charcoal-700 text-blue-400'
                : 'text-charcoal-300 hover:bg-charcoal-700',
            )}
          >
            {isActive && (
              <div className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-blue-400" />
            )}
            <div className="relative">
              <Icon className="h-5 w-5" />
              {badgeCount != null && badgeCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white">
                  {badgeCount}
                </span>
              )}
            </div>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={12}>
          {t(item.labelKey)}
          {badgeCount != null && badgeCount > 0 && ` (${badgeCount})`}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Link
      to={itemHref}
      className={cn(
        'relative flex items-center gap-3 px-3 py-2 text-[13px] font-medium transition-colors duration-100',
        isActive
          ? 'bg-gradient-to-r from-charcoal-700 to-charcoal-750 text-white'
          : 'text-charcoal-200 hover:bg-charcoal-700 hover:text-white',
      )}
    >
      {isActive && (
        <div className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-blue-400" />
      )}
      <Icon className={cn('h-5 w-5 shrink-0', isActive ? 'text-blue-400' : 'text-charcoal-300')} />
      <span className="flex flex-1 items-center justify-between">
        {t(item.labelKey)}
        {badgeCount != null && badgeCount > 0 && (
          <span className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-xs font-bold text-white">
            {badgeCount}
          </span>
        )}
      </span>
    </Link>
  );
}

function GroupSeparator() {
  return <div className="mx-3 my-2 h-px bg-charcoal-700" />;
}

function AdminNavGroupBlock({
  group,
  expanded,
  currentUrl,
  getBadgeCount,
}: {
  group: NavGroup;
  expanded: boolean;
  currentUrl: string;
  getBadgeCount: (badge?: 'quotes' | 'subscriptions' | 'support') => number | undefined;
}) {
  const { t } = useTranslation('admin');

  return (
    <div>
      {expanded && group.labelKey && (
        <p className="mb-1.5 mt-3 px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-charcoal-400">
          {t(group.labelKey)}
        </p>
      )}
      {group.items.map((item) => (
        <AdminRailNavItem
          key={item.href}
          item={item}
          expanded={expanded}
          currentUrl={currentUrl}
          badgeCount={getBadgeCount(item.badge)}
        />
      ))}
    </div>
  );
}

function AdminUserMenuRail({
  expanded,
  onDropdownOpenChange,
}: {
  expanded: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
}) {
  const { user, logout } = useAuth();
  const { t } = useTranslation('admin');

  if (!user) return null;

  const initials = user.name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <DropdownMenu onOpenChange={onDropdownOpenChange}>
      {expanded ? (
        <DropdownMenuTrigger asChild>
          <button className="flex w-full items-center gap-2.5 rounded-md p-1.5 text-left text-charcoal-300 transition-colors hover:bg-charcoal-700 hover:text-white focus:outline-none">
            <Avatar size="sm">
              <AvatarFallback name={user.name} className="text-[10px]">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-white">{user.name}</p>
              <p className="truncate text-[11px] text-charcoal-400">{user.email}</p>
            </div>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-charcoal-400" />
          </button>
        </DropdownMenuTrigger>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button className="mx-auto flex items-center justify-center rounded-md p-1.5 text-charcoal-300 transition-colors hover:bg-charcoal-700 hover:text-white focus:outline-none">
                <Avatar size="sm">
                  <AvatarFallback name={user.name} className="text-[10px]">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={12}>
            {user.name}
          </TooltipContent>
        </Tooltip>
      )}
      <DropdownMenuContent side="right" align="end" sideOffset={8} className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-medium">{user.name}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={adminPath('/profile')}>
            <User />
            {t('my_profile')}
          </Link>
        </DropdownMenuItem>
        <LanguageSwitcher variant="sub-menu" />
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={handleLogout}>
          <LogOut />
          {t('logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AdminSidebar() {
  const [expanded, setExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const shouldCollapseRef = useRef(false);
  const { pathname } = useLocation();
  const { data: pendingCount } = useAdminQuotePendingCount();
  const { data: supportOpenCount } = useAdminSupportOpenCount();
  const { data: dashboardData } = useAdminDashboard('30d');

  const getBadgeCount = (badge?: 'quotes' | 'subscriptions' | 'support'): number | undefined => {
    if (badge === 'quotes') return pendingCount;
    if (badge === 'support') return supportOpenCount || undefined;
    if (badge === 'subscriptions' && dashboardData)
      return dashboardData.kpis.pending_subscriptions || undefined;
    return undefined;
  };

  const handleMouseEnter = () => {
    setExpanded(true);
    shouldCollapseRef.current = false;
  };

  const handleMouseLeave = () => {
    if (dropdownOpen) {
      shouldCollapseRef.current = true;
    } else {
      setExpanded(false);
    }
  };

  const handleDropdownOpenChange = (open: boolean) => {
    setDropdownOpen(open);
    if (!open && shouldCollapseRef.current) {
      setExpanded(false);
      shouldCollapseRef.current = false;
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile : Sheet */}
      <div className="fixed left-4 top-4 z-50 lg:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 bg-charcoal-800 p-0">
            <SheetTitle className="sr-only">Administration</SheetTitle>
            <div className="flex h-14 items-center gap-2 border-b border-charcoal-700 px-4">
              <img src={BRAND.assets.mark} alt={BRAND.name} className="h-6 w-6" />
              <span className="text-lg font-heading font-bold text-blue-400">{BRAND.name}</span>
              <span className="ml-2 text-xs font-medium text-charcoal-400">Admin</span>
            </div>
            <nav className="flex-1 space-y-0.5 overflow-y-auto py-3">
              {adminNavGroups.map((group, idx) => (
                <div key={group.labelKey ?? 'main'}>
                  {idx > 0 && <GroupSeparator />}
                  <AdminNavGroupBlock
                    group={group}
                    expanded
                    currentUrl={pathname}
                    getBadgeCount={getBadgeCount}
                  />
                </div>
              ))}
            </nav>
            <div className="border-t border-charcoal-700 p-2">
              <AdminUserMenuRail expanded />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop : Spacer permanent pour le layout flow */}
      <div className="hidden shrink-0 lg:block" style={{ width: 'var(--sidebar-rail-width)' }} />

      {/* Desktop : Sidebar fixed avec expand au hover */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 hidden h-screen flex-col border-r border-charcoal-700 bg-charcoal-800 transition-all duration-200 ease-out lg:flex',
        )}
        style={{
          width: expanded ? 'var(--sidebar-expanded-width)' : 'var(--sidebar-rail-width)',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Logo zone */}
        <div className="flex h-14 w-full items-center justify-center border-b border-charcoal-700">
          {expanded ? (
            <div className="flex items-center gap-2">
              <img src={BRAND.assets.mark} alt={BRAND.name} className="h-6 w-6" />
              <span className="leading-none text-lg font-heading font-bold text-blue-400">
                {BRAND.name}
              </span>
              <span className="leading-none text-xs font-medium text-charcoal-400">Admin</span>
            </div>
          ) : (
            <img src={BRAND.assets.mark} alt={BRAND.name} className="h-6 w-6" />
          )}
        </div>

        {/* Navigation items */}
        <nav className="flex-1 space-y-0.5 overflow-y-auto py-3">
          {adminNavGroups.map((group, idx) => (
            <div key={group.labelKey ?? 'main'}>
              {idx > 0 && <GroupSeparator />}
              <AdminNavGroupBlock
                group={group}
                expanded={expanded}
                currentUrl={pathname}
                getBadgeCount={getBadgeCount}
              />
            </div>
          ))}
        </nav>

        {/* Bottom zone — User menu */}
        <div className="border-t border-charcoal-700 p-2">
          <AdminUserMenuRail expanded={expanded} onDropdownOpenChange={handleDropdownOpenChange} />
        </div>
      </aside>

      {/* Backdrop overlay quand expanded */}
      {expanded && (
        <div
          className="fixed inset-0 z-30 hidden bg-charcoal-950/10 lg:block"
          onClick={() => setExpanded(false)}
        />
      )}
    </TooltipProvider>
  );
}
